import { z } from "zod";

import { ENTITY_KINDS } from "./constants.ts";

const at = z.iso.datetime();
const requirementValue = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]);

/**
 * The processed, prompt-sized picture of what a user has been doing in one organization. The
 * context service owns it; the API reads it to ground chat turns, and the AI infra can read it
 * directly by subject id.
 */
export const contextSnapshotSchema = z.object({
	subjectId: z.string(),
	userId: z.string(),
	organizationId: z.string(),
	createdAt: at,
	updatedAt: at,
	eventCount: z.number().int().nonnegative(),
	lastSessionId: z.uuid().optional(),
	/** The Company › Project › Solution the user was last working in. */
	focus: z.object({
		companyId: z.string().optional(),
		projectId: z.string().optional(),
		solutionId: z.string().optional()
	}),
	recentPages: z.array(z.object({ path: z.string(), title: z.string().optional(), at })),
	/** Newest first, one entry per entity, with how often it was opened. */
	recentEntities: z.array(
		z.object({
			kind: z.enum(ENTITY_KINDS),
			id: z.string(),
			name: z.string().optional(),
			views: z.number().int().positive(),
			at
		})
	),
	searches: z.array(z.object({ query: z.string(), resultCount: z.number().optional(), at })),
	filters: z.record(z.string(), requirementValue),
	requirements: z.record(z.string(), z.object({ value: requirementValue, at })),
	documents: z.array(z.object({ documentId: z.string(), title: z.string().optional(), at })),
	ctas: z.array(z.object({ ctaId: z.string(), label: z.string().optional(), at })),
	advisorConversations: z.array(z.string()),
	/** Newest first, capped: what makes applying an event idempotent under queue redelivery. */
	appliedEventIds: z.array(z.string()).default([])
});

export type ContextSnapshot = z.infer<typeof contextSnapshotSchema>;
